import { useEffect, useRef } from "react";
import * as THREE from "three";
import { riskOf } from "@/lib/geo";
import { modisUrl } from "@/lib/api";

// realistic top-down sargassum clump textures (transparent PNGs), loaded once & reused
THREE.Cache.enabled = true;
const _texLoader = new THREE.TextureLoader();
const SARG_TEX = ["/brand/sargassum1.webp", "/brand/sargassum2.webp"].map((u) => {
  const t = _texLoader.load(u);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
});

// soft radial drop-shadow sprite (dark -> transparent)
function _shadowTexture() {
  const cv = document.createElement("canvas");
  cv.width = cv.height = 64;
  const c = cv.getContext("2d");
  const g = c.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, "rgba(0,0,0,0.55)");
  g.addColorStop(0.55, "rgba(0,0,0,0.30)");
  g.addColorStop(1, "rgba(0,0,0,0)");
  c.fillStyle = g; c.fillRect(0, 0, 64, 64);
  const t = new THREE.CanvasTexture(cv); t.minFilter = THREE.LinearFilter; return t;
}
// soft foam wake (transparent -> light -> transparent along its length)
function _wakeTexture() {
  const cv = document.createElement("canvas");
  cv.width = 16; cv.height = 64;
  const c = cv.getContext("2d");
  const g = c.createLinearGradient(0, 0, 0, 64);
  g.addColorStop(0, "rgba(205,240,255,0)");
  g.addColorStop(0.5, "rgba(205,240,255,0.6)");
  g.addColorStop(1, "rgba(205,240,255,0)");
  c.fillStyle = g; c.fillRect(0, 0, 16, 64);
  const t = new THREE.CanvasTexture(cv); t.minFilter = THREE.LinearFilter; return t;
}
const SHADOW_TEX = _shadowTexture();
const WAKE_TEX = _wakeTexture();

// -------- top-down (nadir) satellite projection ----------
const UPD = 30;          // world units per degree (to scale)
const DEG_HALF = 4.5;    // half of the vertical degrees shown by the camera
const HORIZON_H = 72;    // hours of real drift replayed per loop
const LOOP_S = 40;       // seconds per loop
const M_PER_DEG = 111320.0;
const CARIB = { w: -90.0, s: 7.0, e: -55.0, n: 26.0 };   // matches backend CARIBBEAN_BBOX

const RISK_HEX = { HIGH: 0xff9f1c, MODERATE: 0xffd166, LOW: 0x22d3ee, MINIMAL: 0x8fb4c9 };

function labelSprite(text, hex, sub) {
  const cv = document.createElement("canvas");
  cv.width = 320; cv.height = 96;
  const ctx = cv.getContext("2d");
  ctx.font = "700 32px 'Syne','JetBrains Mono',sans-serif";
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  const w = ctx.measureText(text).width + 34;
  ctx.fillStyle = "rgba(4,12,26,0.78)";
  ctx.strokeStyle = `#${hex.toString(16).padStart(6, "0")}`;
  ctx.lineWidth = 2.5;
  const rx = 160 - w / 2, ry = 14, rw = w, rh = 44;
  ctx.beginPath(); ctx.roundRect(rx, ry, rw, rh, 8); ctx.fill(); ctx.stroke();
  ctx.fillStyle = "#eef4f8";
  ctx.fillText(text, 160, 32);
  if (sub) {
    ctx.font = "600 17px 'JetBrains Mono',monospace";
    ctx.fillStyle = `#${hex.toString(16).padStart(6, "0")}`;
    ctx.fillText(sub, 160, 74);
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.minFilter = THREE.LinearFilter;
  const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
  sp.scale.set(46, 14, 1);
  return sp;
}

export default function OceanView({ data, selectedIsland, onSelectIsland }) {
  const mountRef = useRef(null);
  const S = useRef({});
  const cbRef = useRef(onSelectIsland);
  const selRef = useRef(selectedIsland);
  cbRef.current = onSelectIsland;
  selRef.current = selectedIsland;

  // ---- one-time scene ----
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const w = mount.clientWidth || 600, h = mount.clientHeight || 600;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x03070f);

    const aspect = w / h;
    const halfV = DEG_HALF * UPD;
    const camera = new THREE.OrthographicCamera(
      -halfV * aspect, halfV * aspect, halfV, -halfV, 0.1, 4000,
    );
    camera.position.set(0, 800, 0);
    camera.up.set(0, 0, -1);       // world -Z (north) points up on screen
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(renderer.domElement);

    const satGroup = new THREE.Group();
    const raftGroup = new THREE.Group();
    const islandGroup = new THREE.Group();
    scene.add(satGroup, raftGroup, islandGroup);

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    Object.assign(S.current, {
      scene, camera, renderer, satGroup, raftGroup, islandGroup,
      raycaster, mouse, rafts: [], islandMeshes: [], hud: null,
      focusPos: { x: 0, z: 0 }, aspect,
    });

    const onClick = (e) => {
      const st = S.current;
      const r = renderer.domElement.getBoundingClientRect();
      st.mouse.x = ((e.clientX - r.left) / r.width) * 2 - 1;
      st.mouse.y = -((e.clientY - r.top) / r.height) * 2 + 1;
      st.raycaster.setFromCamera(st.mouse, st.camera);
      const hit = st.raycaster.intersectObjects(st.islandMeshes, true);
      if (hit.length) {
        let o = hit[0].object;
        while (o && !o.userData.islandId) o = o.parent;
        if (o?.userData.islandId) cbRef.current?.(o.userData.islandId);
      }
    };
    renderer.domElement.addEventListener("pointerdown", onClick);

    const clock = new THREE.Clock();
    let raf;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();
      const st = S.current;

      // subtle top-down "drone hover" — gentle parallax pan around the focus
      const fx = st.focusPos.x, fz = st.focusPos.z;
      st.camera.position.set(fx + Math.sin(t * 0.09) * 6, 800, fz + Math.cos(t * 0.07) * 6);
      st.camera.lookAt(fx, 0, fz);

      // real-scale drift time-lapse
      const phase = (t % LOOP_S) / LOOP_S;
      const hours = phase * HORIZON_H;
      const fade = Math.min(1, phase / 0.10) * Math.min(1, (1 - phase) / 0.12);
      for (const r of st.rafts) {
        const dx = r.vx * hours, dz = r.vz * hours;
        const px = r.x + dx, pz = r.z + dz;
        const bob = 1 + 0.06 * Math.sin(t * 1.5 + r.seed);   // gentle breathing -> floating feel
        r.mesh.position.set(px, 1.6, pz);
        r.mesh.scale.setScalar(bob);
        r.mesh.material.opacity = r.baseOp * fade;
        if (r.shadow) {
          // soft drop shadow, offset by a fixed sun angle so the clump reads as lifted off the water
          r.shadow.position.set(px + 1.8, 0.35, pz + 1.8);
          r.shadow.scale.setScalar(bob * 1.04);
          r.shadow.material.opacity = 0.3 * fade;
        }
        if (r.wake) {
          // trail sits behind the clump, opposite the drift direction
          r.wake.position.set(px - r.ux * r.wlen * 0.5, 0.8, pz - r.uz * r.wlen * 0.5);
          r.wake.material.opacity = 0.12 * fade;
        }
      }
      st.islandMeshes.forEach((m) => {
        const sel = m.userData.islandId === selRef.current;
        const ring = m.userData.ring;
        if (ring) {
          const pulse = 0.55 + 0.45 * Math.sin(t * 2.2 + m.position.x);
          ring.material.opacity = (sel ? 0.9 : 0.5) * pulse;
          const sc = sel ? 1.0 + 0.08 * Math.sin(t * 3) : 1.0;
          ring.scale.setScalar(sc);
        }
      });
      st.hud && st.hud(hours);
      st.renderer.render(st.scene, st.camera);
    };
    animate();

    const ro = new ResizeObserver(() => {
      const nw = mount.clientWidth, nh = mount.clientHeight;
      if (!nw || !nh) return;
      const st = S.current;
      const asp = nw / nh;
      st.aspect = asp;
      st.camera.left = -DEG_HALF * UPD * asp;
      st.camera.right = DEG_HALF * UPD * asp;
      st.camera.top = DEG_HALF * UPD;
      st.camera.bottom = -DEG_HALF * UPD;
      st.camera.updateProjectionMatrix();
      st.renderer.setSize(nw, nh);
    });
    ro.observe(mount);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      renderer.domElement.removeEventListener("pointerdown", onClick);
      renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- rebuild satellite backdrop + rafts + islands when data / focus changes ----
  useEffect(() => {
    const st = S.current;
    if (!st.scene || !data?.islands?.length) return;
    const focus = data.islands.find((i) => i.id === selectedIsland) || data.islands[0];
    const lat0 = focus.lat, lng0 = focus.lng;
    const cx = Math.cos((lat0 * Math.PI) / 180);
    const xOf = (lng) => (lng - lng0) * UPD * cx;
    const zOf = (lat) => (lat0 - lat) * UPD;
    st.focusPos = { x: xOf(focus.lng), z: zOf(focus.lat) };

    // --- real GIBS satellite ocean backdrop, geographically aligned ---
    st.satGroup.clear();
    const Wx = (CARIB.e - CARIB.w) * UPD * cx;
    const Wz = (CARIB.n - CARIB.s) * UPD;
    const planeCx = xOf((CARIB.w + CARIB.e) / 2);
    const planeCz = zOf((CARIB.s + CARIB.n) / 2);
    const satMat = new THREE.MeshBasicMaterial({ color: 0x0a1420 });
    const satPlane = new THREE.Mesh(new THREE.PlaneGeometry(Wx, Wz), satMat);
    satPlane.rotation.x = -Math.PI / 2;
    satPlane.position.set(planeCx, -0.5, planeCz);
    st.satGroup.add(satPlane);
    new THREE.TextureLoader().load(
      modisUrl({ width: 2048, height: 1024 }),
      (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        satMat.map = tex;
        satMat.color.set(0xdfe8f0);
        satMat.needsUpdate = true;
      },
    );

    // real drift field from flowlines (NOAA current + NASA wind)
    const flows = (data.flowlines || []).map((f) => ({
      lat: f.start.lat, lng: f.start.lng,
      u: ((f.end.lng - f.start.lng) / 1.1) * f.speed,
      v: ((f.end.lat - f.start.lat) / 1.1) * f.speed,
    }));
    const driftAt = (lat, lng) => {
      if (!flows.length) return { u: 0, v: 0 };
      let best = flows[0], bd = 1e9;
      for (const fl of flows) {
        const d = (fl.lat - lat) ** 2 + (fl.lng - lng) ** 2;
        if (d < bd) { bd = d; best = fl; }
      }
      return best;
    };

    // ---- rafts: real AFAI mats rendered as scattered sargassum imagery, drifting to scale ----
    st.raftGroup.clear();
    st.rafts = [];
    const belt = (data.belt || []).filter(
      (p) => p.lat >= CARIB.s && p.lat <= CARIB.n && p.lng >= CARIB.w && p.lng <= CARIB.e,
    );
    const maxD = belt.reduce((m, p) => Math.max(m, p.density), 0.001);
    let sprites = 0;
    const SPRITE_CAP = 2000;   // clump budget (×3 meshes each: shadow+wake+clump)
    belt.forEach((p, i) => {
      // density mapping tied to the REAL AFAI reading: coverage, clump count & size all scale
      // with the observed biomass (light readings -> a lone small wisp, dense mats -> a full patch)
      const dn = Math.min(1, p.density / maxD);
      const dnv = Math.pow(dn, 0.8);
      const latR = (p.lat * Math.PI) / 180;
      const dr = driftAt(p.lat, p.lng);
      const vx = ((dr.u * 3600) / (M_PER_DEG * Math.cos(latR))) * UPD * cx; // units / hour (to scale)
      const vz = -((dr.v * 3600) / M_PER_DEG) * UPD;
      const spd = Math.hypot(vx, vz);
      const ux = spd > 1e-4 ? vx / spd : 0;
      const uz = spd > 1e-4 ? vz / spd : 1;
      const wlen = Math.max(4, Math.min(34, spd * 10));
      const bx = xOf(p.lng), bz = zOf(p.lat);
      const clumps = 1 + Math.round(dnv * 5);            // 1..6 clumps by real density
      const spread = 2.0 + dnv * 5.5;                    // denser mats spread over a wider footprint
      for (let k = 0; k < clumps && sprites < SPRITE_CAP; k++) {
        const tex = SARG_TEX[(i + k) % SARG_TEX.length];
        const size = (2.2 + dnv * 4.2) * (0.6 + Math.random() * 0.85);
        const ox = (Math.random() - 0.5) * spread * 2;
        const oz = (Math.random() - 0.5) * spread * 2;
        const cxp = bx + ox, czp = bz + oz;

        // soft drop shadow (drawn first, under the clump)
        const shadow = new THREE.Mesh(
          new THREE.PlaneGeometry(size * 1.25, size * 1.25),
          new THREE.MeshBasicMaterial({ map: SHADOW_TEX, transparent: true, opacity: 0.3, depthWrite: false }),
        );
        shadow.rotation.x = -Math.PI / 2;

        // wake trail aligned to the drift, sitting behind the clump
        const wake = new THREE.Mesh(
          new THREE.PlaneGeometry(size * 0.7, wlen),
          new THREE.MeshBasicMaterial({ map: WAKE_TEX, transparent: true, opacity: 0.12, side: THREE.DoubleSide, depthWrite: false }),
        );
        wake.rotation.x = -Math.PI / 2;
        wake.rotation.z = Math.atan2(vx, vz);

        // realistic seaweed clump
        const geo = new THREE.PlaneGeometry(size, size);
        geo.rotateZ(Math.random() * Math.PI * 2);        // random in-plane spin (verts + UVs)
        const mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({
          map: tex, transparent: true, opacity: 0.95, side: THREE.DoubleSide,
          depthWrite: false, alphaTest: 0.16,
        }));
        mesh.rotation.x = -Math.PI / 2;

        st.raftGroup.add(shadow, wake, mesh);            // order = shadow < wake < clump
        st.rafts.push({ mesh, shadow, wake, x: cxp, z: czp, vx, vz, ux, uz, wlen,
                        baseOp: 0.95, seed: (i + k) * 1.7 });
        sprites++;
      }
    });

    // ---- island markers (risk-coloured rings + labels over the real imagery) ----
    st.islandGroup.clear();
    st.islandMeshes = [];
    data.islands
      .filter((i) => i.lat >= CARIB.s && i.lat <= CARIB.n && i.lng >= CARIB.w && i.lng <= CARIB.e)
      .forEach((isl) => {
        const hex = RISK_HEX[isl.risk] ?? RISK_HEX.MINIMAL;
        const g = new THREE.Group();
        const dot = new THREE.Mesh(
          new THREE.CircleGeometry(3.2, 24),
          new THREE.MeshBasicMaterial({ color: hex, transparent: true, opacity: 0.9, depthWrite: false }),
        );
        dot.rotation.x = -Math.PI / 2; dot.position.y = 1.4;
        const ring = new THREE.Mesh(
          new THREE.RingGeometry(4.5, 6.0, 40),
          new THREE.MeshBasicMaterial({ color: hex, transparent: true, opacity: 0.6, side: THREE.DoubleSide, depthWrite: false }),
        );
        ring.rotation.x = -Math.PI / 2; ring.position.y = 1.3;
        const lbl = labelSprite(isl.name, hex, isl.risk);
        lbl.position.set(0, 6, isl.id === focus.id ? -10 : -8);
        g.add(dot, ring, lbl);
        g.position.set(xOf(isl.lng), 0, zOf(isl.lat));
        g.userData.islandId = isl.id;
        g.userData.ring = ring;
        st.islandGroup.add(g);
        st.islandMeshes.push(g);
      });
  }, [data, selectedIsland]);

  const hudRef = useRef(null);
  const barRef = useRef(null);
  useEffect(() => {
    S.current.hud = (hours) => {
      if (hudRef.current) hudRef.current.textContent = `+${Math.round(hours)}h`;
      if (barRef.current) barRef.current.style.width = `${(hours / HORIZON_H) * 100}%`;
    };
  }, []);

  const focus = (data?.islands || []).find((i) => i.id === selectedIsland) || (data?.islands || [])[0];
  const inView = (data?.belt || []).filter(
    (p) => p.lat >= CARIB.s && p.lat <= CARIB.n && p.lng >= CARIB.w && p.lng <= CARIB.e,
  ).length;

  return (
    <div ref={mountRef} className="relative h-full w-full" data-testid="ocean-view">
      <div className="pointer-events-none absolute inset-0 z-10">
        {/* provenance */}
        <div className="absolute left-3 top-14 rounded-md border border-emerald-400/25 bg-black/45 px-2.5 py-1.5 backdrop-blur">
          <div className="flex items-center gap-1.5">
            <span className="live-dot h-1.5 w-1.5 rounded-full bg-emerald-400" />
            <span className="font-mono-tel text-[11px] font-bold tracking-widest text-emerald-300">TOP-DOWN · REAL SATELLITE</span>
          </div>
          <div className="font-mono-tel text-[10px] text-slate-300">
            {inView} real sargassum rafts · drift = <span className="text-cyanx">NOAA current + wind</span> · base = <span className="text-cyanx">NASA GIBS</span>
          </div>
        </div>

        {/* legend */}
        <div className="absolute left-3 bottom-14 space-y-1 rounded-md border border-white/10 bg-black/45 px-2.5 py-1.5 backdrop-blur">
          <div className="flex items-center gap-2 text-[10px] text-slate-300">
            <img src="/brand/sargassum1.webp" alt="" className="h-3.5 w-3.5 object-contain" />
            Real sargassum mats — drifting to scale
          </div>
          <div className="flex items-center gap-2 text-[10px] text-slate-300">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#22d3ee" }} />
            Island — risk-coloured marker
          </div>
        </div>

        {/* forecast clock */}
        <div className="absolute right-3 bottom-14 w-40 rounded-md border border-cyan-400/25 bg-black/45 px-2.5 py-1.5 text-right backdrop-blur">
          <div className="eyebrow text-slate-500">Forecast time-lapse</div>
          <div ref={hudRef} className="font-mono-tel text-lg font-bold text-cyanx">+0h</div>
          <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-white/10">
            <div ref={barRef} className="h-full rounded-full bg-cyan-400/80" style={{ width: "0%" }} />
          </div>
          <div className="mt-0.5 font-mono-tel text-[9px] text-slate-500">0 → 72h · real scale</div>
        </div>

        {focus && (
          <div className="absolute right-3 top-14 rounded-md border px-2.5 py-1 backdrop-blur"
            style={{ borderColor: `${riskOf(focus.risk).hex}66`, background: "rgba(0,0,0,0.45)" }}>
            <div className="font-mono-tel text-xs font-bold" style={{ color: riskOf(focus.risk).hex }}>
              {focus.name} · {focus.risk}
            </div>
            <div className="eyebrow text-slate-400">
              {focus.eta_utc ? `arrival +${Math.round(focus.eta_lo ?? 0)}–${Math.round(focus.eta_hi ?? 0)}h` : "no landfall in 72h"}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
